'use client';
import { useEffect } from 'react';
import { useHandoffArrival } from '../hooks/useHandoffArrival';

export interface HandoffArrivalToastProps {
  /** Auto-dismiss after this many ms. Default 5000. Pass 0 to disable. */
  autoDismissMs?: number;
}

/**
 * Drop-in arrival banner — pair with the F2 → F5 handoff flow. Renders nothing
 * when there's no `?_toast=` to show.
 */
export function HandoffArrivalToast({ autoDismissMs = 5_000 }: HandoffArrivalToastProps) {
  const { toast, dismiss } = useHandoffArrival();

  useEffect(() => {
    if (!toast || autoDismissMs <= 0) return;
    const t = setTimeout(dismiss, autoDismissMs);
    return () => clearTimeout(t);
  }, [toast, autoDismissMs, dismiss]);

  if (!toast) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        background: '#111',
        color: '#fff',
        padding: '10px 16px',
        borderRadius: 8,
        boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
        fontFamily: 'system-ui, sans-serif',
        fontSize: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <span>{toast}</span>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          background: 'transparent',
          border: 'none',
          color: '#fff',
          cursor: 'pointer',
          fontSize: 16,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}
